import React, { useEffect, useMemo, useRef, useState } from "react";
import ReviewAccordionRenderer from "./ReviewAccordionRenderer";
import {
  canSearch,
  buildSearchableDataset,
  clearSearchHighlights,
  highlightMatchingElements,
  openAncestorAccordions,
} from "./reviewSummaryUtils";
import ReviewSearchEmptyState from "../../../Components/ReviewSummarySearch/ReviewSearchEmptyState";
import { UserStateContext } from "../../../Context/AuthContext";

// import ACH from "./AutomatedClearingHouse";
// import MobileTextMessage from "./MobileTextMessage";
// import SSRP from "./NightlyFiles";

const ReviewSummary = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  setActiveStep,
  signatureFormData,
  i2cStakeHolderSignOff,
  showFormSignature,
  type = "debit",
  searchQuery = "",
}) => {
  const appContext = UserStateContext();
  const clientUser = String(appContext.userRole) === "5" || appContext.roleName === "client";
  const containerRef = useRef(null);
  const isSearchActiveRef = useRef(false);
  const [searchStatus, setSearchStatus] = useState({
    isActive: false,
    hasResults: true,
  });

  const accordionSections = useMemo(
    () => [
      { sectionId: "0", summary: "Project & Client Information" },
      { sectionId: "1", summary: "Program Information" },
      { sectionId: "2", summary: "Card Network And Bin Info" },
      { sectionId: "3", summary: "Interfaces and Components" },
      { sectionId: "4", summary: "Value Added Services" },
      { sectionId: "5", summary: "Connectivity & File Exchange" },
      { sectionId: "6", summary: "Web Services API" },
      { sectionId: "7", summary: "Cooperative Authentication" },
      { sectionId: "8", summary: "Settlement & Reconciliation" },
      { sectionId: "9", summary: "Additional Comments" },
      { sectionId: "10", summary: "Use Case Document" },
      { sectionId: "11", summary: "Client Signoff" },
      {
        sectionId: "12",
        summary: "I2C Internal Stakeholder Review & Sign Off",
      },
    ],
    [],
  );

  const searchableData = useMemo(
    () => buildSearchableDataset(addFormData, accordionSections),
    [accordionSections, addFormData],
  );

  useEffect(() => {
    return handleAccordion();
  }, []);

  function handleAccordion() {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleShow = (event) => {
      if (isSearchActiveRef.current) return;

      const accordion = container.querySelectorAll("i2c-accordion");
      for (let i = 0; i < accordion.length; i++) {
        if (accordion[i] !== event.target) {
          accordion[i].open = false;
        }
      }
    };

    container.addEventListener("i2c-show", handleShow);
    return () => container.removeEventListener("i2c-show", handleShow);
  }

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    clearSearchHighlights(container);

    const normalizedQuery = searchQuery.trim().toLowerCase();
    const canRunSearch = canSearch(normalizedQuery);
    isSearchActiveRef.current = canRunSearch;

    if (!canRunSearch) {
      setSearchStatus({ isActive: false, hasResults: true });
      return;
    }

    const dataSectionIds = searchableData
      .filter(({ searchableText }) => searchableText.includes(normalizedQuery))
      .map(({ sectionId }) => sectionId);

    const accordionMatches = Array.from(
      container.querySelectorAll("[data-review-section]"),
    ).filter((accordion) => {
      const sectionId = accordion.dataset.reviewSection;
      const hasDataMatch = dataSectionIds.includes(sectionId);
      const hasRenderedMatch = (accordion.textContent || "")
        .toLowerCase()
        .includes(normalizedQuery);

      return hasDataMatch || hasRenderedMatch;
    });

    setSearchStatus({
      isActive: true,
      hasResults: accordionMatches.length > 0,
    });

    if (!accordionMatches.length) return;

    const [firstMatch] = accordionMatches;
    Array.from(container.querySelectorAll("[data-review-section]")).forEach(
      (accordion) => {
        accordion.open = accordionMatches.includes(accordion);
      },
    );

    accordionMatches.forEach((accordion) => {
      accordion.classList.add("review-search-section-match");
    });

    const highlightTimeout = setTimeout(() => {
      clearSearchHighlights(container);
      accordionMatches.forEach((accordion) => {
        accordion.classList.add("review-search-section-match");
      });

      const highlightedElements = highlightMatchingElements(
        accordionMatches,
        normalizedQuery,
      );
      const scrollTarget = highlightedElements[0] || firstMatch;
      openAncestorAccordions(scrollTarget);
      scrollTarget.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);

    return () => clearTimeout(highlightTimeout);
  }, [searchQuery, searchableData]);

  return (
    <React.Fragment>
      <div className="App">
        <div
          ref={containerRef}
          className="accordion-group-example"
          style={{ width: "100%" }}
        >
          <div i2c-grid="grid gap-sm">
            <div i2c-grid="12">
              {searchStatus.isActive && !searchStatus.hasResults && (
                <ReviewSearchEmptyState />
              )}
              <ReviewAccordionRenderer
                formData={addFormData}
                signatureFormData={signatureFormData}
                i2cStakeHolderSignOff={i2cStakeHolderSignOff}
                type={type}
                editable={true}
                onEdit={setActiveStep}
                showClientSignoff={showFormSignature}
                clientUser={clientUser}
                accordionSections={accordionSections}
                variant="striped"
              />
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ReviewSummary;
